import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  CheckingAccountState, CheckingAccountUtilsService,
  CompanyState,
  SetCheckingAccountsAction
} from '@celerofinancas/state-management';
import { WebsocketService } from '@celerofinancas/ui-websocket';
import { Store } from '@ngxs/store';
import { BalanceResponse } from '@pages/dashboard-finance/dashboard-finance.interfaces';
import {
  Observable,
  Subject,
  catchError,
  combineLatest,
  delay,
  distinctUntilChanged,
  filter,
  of,
  retry,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

/**
 * Service responsible for managing WebSocket connections and balance updates
 * Follows Single Responsibility Principle by handling only WebSocket-related logic
 */
@Injectable()
export class BalanceWebsocketService implements OnDestroy {
  /**
   * WebSocket service instance
   */
  private readonly websocketService = inject(WebsocketService);

  /**
   * Checking accounts store utilities
   */
  private utils = inject(CheckingAccountUtilsService);

  /**
   * State management store
   */
  private readonly store = inject(Store);

  /**
   * Subject to handle component destruction and cleanup
   */
  private readonly destroy$ = new Subject<void>();

  /**
   * Subject to track WebSocket connection errors
   */
  private readonly errors$ = new Subject<Error>();

  /**
   * Maximum retry attempts for WebSocket connection
   */
  private readonly MAX_RETRY_ATTEMPTS = 3;

  /**
   * Delay in milliseconds before joining room after connection
   * This gives the backend time to setup RabbitMQ queues
   */
  private readonly JOIN_DELAY_MS = 500;

  /**
   * Initializes WebSocket connection and starts listening to balance changes
   */
  public initialize(): void {
    this.connectWebSocket();
    this.listenToBalanceUpdates();
  }

  /**
   * Establishes WebSocket connection when company is available
   * @private
   */
  private connectWebSocket(): void {
    this.store
      .select(CompanyState.getActiveCompanyPk)
      .pipe(
        filter((companyPk) => !!companyPk),
        distinctUntilChanged(),
        switchMap((companyPk) => this.establishConnection(String(companyPk))),
        retry({
          count: this.MAX_RETRY_ATTEMPTS,
          delay: 2000,
        }),
        catchError((error) => {
          this.handleConnectionError(error);
          return of(null);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  /**
   * Establishes connection to WebSocket and joins company room
   * @param companyPk - The company primary key
   * @private
   */
  private establishConnection(companyPk: string): Observable<string> {
    return combineLatest([
      this.websocketService.status$(),
      of(companyPk),
    ]).pipe(
      filter(([status]) => status === 'connected'),
      // Add delay to allow backend RabbitMQ queue setup
      delay(this.JOIN_DELAY_MS),
      tap(([_, companyPk]) => {
        this.websocketService.join({ companyId: companyPk });
      }),
      switchMap(([status]) => of(status)),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Listens to balance updates from WebSocket and updates the state
   * @private
   */
  private listenToBalanceUpdates(): void {
    this.websocketService
      .on<BalanceResponse>('balance')
      .pipe(
        filter((response) => this.isValidBalanceResponse(response)),
        catchError((error) => {
          this.handleBalanceUpdateError(error);
          return of(null);
        }),
        filter((response): response is BalanceResponse => response !== null),
        takeUntil(this.destroy$)
      )
      .subscribe((response) => this.updateCheckingAccountBalance(response));
  }

  /**
   * Validates balance response structure
   * @param response - The balance response to validate
   * @private
   */
  private isValidBalanceResponse(response: BalanceResponse): boolean {
    if (!response?.data) {
      return false;
    }

    const { bank_account_id, balance } = response.data;

    if (!bank_account_id) {
      return false;
    }

    if (balance === undefined || balance === null) {
      return false;
    }

    return true;
  }

  /**
   * Updates checking account balance in the state
   * Implements the complete tap logic including company change detection and balance preservation
   * @param response - The balance response from WebSocket
   * @private
   */
  private updateCheckingAccountBalance(response: BalanceResponse): void {
    const state: any = this.store.selectSnapshot(CheckingAccountState.getState);
    const stateList = Array.isArray(state) ? state : state?.list;

    if (!stateList || !Array.isArray(stateList)) {
      console.warn('[BalanceWebsocket] Invalid checking accounts state');
      return;
    }

    // Parse the balance from WebSocket
    const parsedBalance = this.safeParseBalance(response.data.balance);
    const parseBlockedBalance = this.safeParseBalance(response.data.blocked_balance);

    if (parsedBalance === null) {
      console.warn('[BalanceWebsocket] Failed to parse balance:', response.data.balance);
      return;
    }

    if (parseBlockedBalance === null) {
      console.warn('[BalanceWebsocket] Failed to parse blocked balance:', response.data.blocked_balance);
    }

    // Create a temporary results array with the updated balance
    const results = stateList.map((account: any) => {
      if (account.pk === response.data.bank_account_id) {
        return {
          ...account,
          bank_balance: parsedBalance,
          bank_account_current_balance: parsedBalance,
          last_statement_balance: String(parsedBalance),
          blocked_balance: parseBlockedBalance,
          mirrorBalanceLastUpdate: new Date().toISOString(),
        };
      }
      return account;
    });

    this.store.dispatch(new SetCheckingAccountsAction(results));
  }


  /**
   * Safely parses balance string to number
   * @param balance - Balance string to parse
   * @private
   */
  private safeParseBalance(balance: string): number | null {
    const parsed = Number(balance);

    if (isNaN(parsed)) {
      return null;
    }

    return Math.round(parsed);
  }

  /**
   * Handles WebSocket connection errors
   * @param error - The error that occurred
   * @private
   */
  private handleConnectionError(error: Error): void {
    this.errors$.next(error);
  }

  /**
   * Handles balance update errors
   * @param error - The error that occurred
   * @private
   */
  private handleBalanceUpdateError(error: Error): void {
    this.errors$.next(error);
  }

  /**
   * Observable for error tracking
   */
  public getErrors$(): Observable<Error> {
    return this.errors$.asObservable();
  }

  /**
   * Disconnects WebSocket and cleans up resources
   */
  public disconnect(): void {
    this.websocketService.disconnect();
  }

  /**
   * Cleanup on service destruction
   */
  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.errors$.complete();
    this.disconnect();
  }
}

